import { useContext, useCallback, useState, useEffect } from "react";

import { OrgRecord } from "@/shared/domain/orgs/OrgRecord";

import { Auth, AuthContext } from "./AuthContext";

export const useAuth = (): Auth => {
  return useContext(AuthContext);
};

const SYSTEM_USER_OVERRIDE_KEY = "roboto_override_system_user_view";

export const useSystemUser = () => {
  const { currentUser } = useAuth();
  const [isOverrideEnabled, setIsOverrideEnabled] = useState(
    () => localStorage.getItem(SYSTEM_USER_OVERRIDE_KEY) === "true",
  );

  const isActualSystemUser = currentUser?.is_system_user ?? false;

  // Listen for storage changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      setIsOverrideEnabled(
        localStorage.getItem(SYSTEM_USER_OVERRIDE_KEY) === "true",
      );
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const toggleSystemUserOverride = useCallback(() => {
    if (!isActualSystemUser) {
      return;
    }

    const newOverrideState = !isOverrideEnabled;
    if (newOverrideState) {
      localStorage.setItem(SYSTEM_USER_OVERRIDE_KEY, "true");
    } else {
      localStorage.removeItem(SYSTEM_USER_OVERRIDE_KEY);
    }

    setIsOverrideEnabled(newOverrideState);
    // Keep the storage event for other tabs/windows
    window.dispatchEvent(new Event("storage"));
  }, [isActualSystemUser, isOverrideEnabled]);

  return {
    isSystemUser: isActualSystemUser && !isOverrideEnabled,
    isActualSystemUser,
    isOverrideEnabled,
    toggleSystemUserOverride,
  };
};

/** A role in an org, lowest first; each may do what the roles below it may. */
export type OrgRole = "user" | "admin" | "owner";

const ORG_ROLE_RANK: Record<OrgRole, number> = { user: 0, admin: 1, owner: 2 };

export interface OrgRoleOptions {
  /** The org to check. Defaults to the current org. */
  orgId?: string;
  /**
   * Whether a Roboto system user qualifies whatever their role in the org,
   * including in an org they are not a member of. Defaults to true. The
   * "view as a regular user" override still applies. Pass false for a control
   * staff should not see in a customer's org even though the backend would
   * accept it from them, such as the org's Delete button.
   */
  systemUsers?: boolean;
}

/**
 * Whether the signed-in user may act as `role` in an org: they hold that role
 * or a higher one there, or they are a Roboto system user (see
 * {@link OrgRoleOptions.systemUsers}). False while roles are loading.
 *
 * The one place the web UI decides org-admin and org-owner access; gate UI
 * through this or {@link OrgRoleOnly}, never by reading `roles` directly.
 */
export const useHasOrgRole = (
  role: OrgRole,
  { orgId, systemUsers = true }: OrgRoleOptions = {},
): boolean => {
  const { currentOrganization, myOrgRoles } = useAuth();
  const { isSystemUser } = useSystemUser();
  if (systemUsers && isSystemUser) {
    return true;
  }
  const targetOrgId = orgId ?? currentOrganization?.org_id;
  const roles = myOrgRoles?.find(
    (entry) => entry.org.org_id === targetOrgId,
  )?.roles;
  return (
    roles?.some(
      (held) =>
        held in ORG_ROLE_RANK &&
        ORG_ROLE_RANK[held as OrgRole] >= ORG_ROLE_RANK[role],
    ) ?? false
  );
};

export const useCurrentOrg = (): OrgRecord | null => {
  const auth = useAuth();
  return auth.currentOrganization;
};

export const useCurrentOrgId = (
  // This is optional so components which use this hook can fire an optional prop into this without doing any
  // further validation, and verify that it'll resolve to something meaningful.
  overrideOrgId?: string,
): string => {
  const auth = useAuth();

  if (overrideOrgId) {
    return overrideOrgId;
  }

  if (auth.currentOrganization?.org_id) {
    return auth.currentOrganization.org_id;
  }

  throw new Error(
    "Could not resolve an org_id from useAuth organization or from overrideOrgId",
  );
};
